import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
    getExpertProfileData,
    getExpertsList,
    getExportExpertsDetails,
    getFilterOptions,
    getSkills,
} from 'src/services/project.service'
import { message } from 'antd'
import { FilteredTag } from './useTag'
import { ExpertTableDataType } from 'src/types/api.types'

export const useExpertsData = (
    expertId: string,
    tagsList: FilteredTag[],
    pagination: { page: number; pageSize: number },
    selectedRows: ExpertTableDataType[]
) => {
    const filtersOptionsQuery = useQuery({
        queryKey: ['filtersOptions'],
        queryFn: () => getFilterOptions(),
    })

    const skillsListQuery = useQuery({
        queryKey: ['skillsList'],
        queryFn: () => getSkills(),
    })

    const expertsProfileQuery = useQuery({
        queryKey: ['expertsProfile', expertId],
        queryFn: () => getExpertProfileData(expertId),
        enabled: !!expertId,
    })

    const expertsListQuery = useQuery({
        queryKey: ['expertsList', tagsList, pagination],
        queryFn: () => {
            return getExpertsList(
                tagsList,
                pagination.pageSize * (pagination.page - 1),
                pagination.pageSize
            )
        },
        enabled: tagsList.length > 0 && pagination.page > 0,
    })

    const exportDetailsQuery = useQuery({
        queryKey: ['exportDetails'],
        queryFn: () => getExportExpertsDetails(selectedRows.map(val => val.key)),
        enabled: false, // disable to call the api on page load
    })

    useEffect(() => {
        if (expertsListQuery.error) {
            message.error('Failed to load requested experts list. Please try again!', 2)
            return
        }

        if (filtersOptionsQuery.error || skillsListQuery.error || expertsProfileQuery.error) {
            message.error('Failed to load filters list. Please try again!', 2)
            return
        }

        if (exportDetailsQuery.error) {
            message.error('Failed to load experts profile. Please try again!', 2)
        }
    }, [
        expertsListQuery.error,
        filtersOptionsQuery.error,
        skillsListQuery.error,
        expertsProfileQuery.error,
        exportDetailsQuery.error,
    ])

    return {
        filtersOptionsQuery,
        skillsListQuery,
        expertsProfileQuery,
        expertsListQuery,
        exportDetailsQuery,
    }
}
